import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import { DashboardLayout } from '@/components/dashboard/DashboardLayout'
import { useAuth } from '@/components/auth/AuthProvider'
import { useOrganization, useUpdateOrganization } from '@/hooks/useOrganization'
import { PricingTable, SubscriptionStatus } from '@/components/billing'
import { stripeService } from '@/services/stripe.service'
import { extractionService } from '@/services/extraction.service'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Switch } from '@/components/ui/switch'
import { CheckCircle, Key, Eye, EyeOff, Upload } from 'lucide-react'
import { toast } from 'sonner'

const profileSchema = z.object({
  full_name: z.string().min(2, 'Name must be at least 2 characters')
})

const organizationSchema = z.object({
  name: z.string().min(2, 'Organization name must be at least 2 characters')
})

type ProfileForm = z.infer<typeof profileSchema>
type OrganizationForm = z.infer<typeof organizationSchema>

export default function Settings() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { user, dbUser, refreshUser } = useAuth()
  const { data: organization } = useOrganization()
  const updateOrgMutation = useUpdateOrganization()
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false)
  const [showSuccessAlert, setShowSuccessAlert] = useState(false)
  
  // API Keys state
  const [nanonetsApiKey, setNanonetsApiKey] = useState('')
  const [nanonetsModelId, setNanonetsModelId] = useState('')
  const [showApiKey, setShowApiKey] = useState(false)
  
  // Permissions state
  const [uploadEnabled, setUploadEnabled] = useState(false)
  
  // Load initial config
  useEffect(() => {
    const config = extractionService.getConfig()
    setNanonetsModelId(config.modelId)
    setUploadEnabled(extractionService.isUploadEnabled())
  }, [])

  const { data: subscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => stripeService.getSubscriptionStatus()
  })

  // Handle success/cancel from Stripe checkout
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setShowSuccessAlert(true)
      toast.success('Subscription activated!')
      searchParams.delete('success')
      setSearchParams(searchParams)
    }
    if (searchParams.get('canceled') === 'true') {
      toast.info('Checkout canceled')
      searchParams.delete('canceled')
      setSearchParams(searchParams)
    }
  }, [searchParams, setSearchParams])

  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      full_name: dbUser?.full_name || ''
    }
  })

  const orgForm = useForm<OrganizationForm>({
    resolver: zodResolver(organizationSchema),
    defaultValues: {
      name: organization?.name || ''
    }
  })

  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'U'
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const handleProfileUpdate = async (data: ProfileForm) => {
    setIsUpdatingProfile(true)
    try {
      // Note: Actual profile update would use a user service
      // This is a placeholder for the full implementation
      await refreshUser()
      toast.success('Profile updated')
    } catch (err) {
      toast.error('Failed to update profile')
    } finally {
      setIsUpdatingProfile(false)
    }
  }

  const handleOrgUpdate = async (data: OrganizationForm) => {
    if (!organization) return

    try {
      await updateOrgMutation.mutateAsync({
        id: organization.id,
        updates: { name: data.name }
      })
      toast.success('Organization updated')
    } catch (err) {
      toast.error('Failed to update organization')
    }
  }

  const handleApiKeysSave = () => {
    extractionService.setConfig({
      apiKey: nanonetsApiKey || undefined,
      modelId: nanonetsModelId || undefined
    })
    toast.success('API configuration saved for this session')
  }

  return (
    <DashboardLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Settings</h1>
          <p className="text-muted-foreground">
            Manage your account and organization settings
          </p>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList>
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="organization">Organization</TabsTrigger>
            <TabsTrigger value="api-keys">API Keys</TabsTrigger>
            <TabsTrigger value="billing">Billing</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>
                  Update your personal information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center gap-4">
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={dbUser?.avatar_url || undefined} />
                    <AvatarFallback className="text-lg">
                      {getInitials(dbUser?.full_name || user?.email)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">
                      {dbUser?.full_name || 'No name set'}
                    </p>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                  </div>
                </div>

                <Separator />

                <form
                  onSubmit={profileForm.handleSubmit(handleProfileUpdate)}
                  className="space-y-4"
                >
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name</Label>
                    <Input
                      id="full_name"
                      {...profileForm.register('full_name')}
                    />
                    {profileForm.formState.errors.full_name && (
                      <p className="text-sm text-destructive">
                        {profileForm.formState.errors.full_name.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" value={user?.email || ''} disabled />
                    <p className="text-xs text-muted-foreground">
                      Email cannot be changed
                    </p>
                  </div>

                  <Button type="submit" disabled={isUpdatingProfile}>
                    {isUpdatingProfile ? 'Saving...' : 'Save Changes'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="organization" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Organization Settings</CardTitle>
                <CardDescription>
                  Manage your organization details
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form
                  onSubmit={orgForm.handleSubmit(handleOrgUpdate)}
                  className="space-y-4"
                >
                  <div className="space-y-2">
                    <Label htmlFor="org_name">Organization Name</Label>
                    <Input id="org_name" {...orgForm.register('name')} />
                    {orgForm.formState.errors.name && (
                      <p className="text-sm text-destructive">
                        {orgForm.formState.errors.name.message}
                      </p>
                    )}
                  </div>

                  <Button type="submit" disabled={updateOrgMutation.isPending}>
                    {updateOrgMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Team Members</CardTitle>
                <CardDescription>
                  Manage your organization members
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Team management coming soon
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="api-keys" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  Nanonets OCR Configuration
                </CardTitle>
                <CardDescription>
                  Configure your Nanonets API credentials for document OCR processing.
                  Keys are stored in your browser session only.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nanonets-key">Nanonets API Key</Label>
                  <div className="flex gap-2">
                    <Input
                      id="nanonets-key"
                      type={showApiKey ? 'text' : 'password'}
                      placeholder="Enter your Nanonets API key..."
                      value={nanonetsApiKey}
                      onChange={(e) => setNanonetsApiKey(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setShowApiKey(!showApiKey)}
                    >
                      {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Get your API key from{' '}
                    <a
                      href="https://app.nanonets.com/#/keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline"
                    >
                      app.nanonets.com
                    </a>
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nanonets-model">Model ID</Label>
                  <Input
                    id="nanonets-model"
                    placeholder="e.g., 768fe153-a6f2-4b98-b2e7-14444bfa8e1d"
                    value={nanonetsModelId}
                    onChange={(e) => setNanonetsModelId(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Find your Model ID in Nanonets dashboard → Your Model → Settings.
                    Leave empty to use general OCR.
                  </p>
                </div>

                <Button onClick={handleApiKeysSave}>
                  Save Configuration
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  Upload Permissions
                </CardTitle>
                <CardDescription>
                  Control who can upload documents to the system.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Enable Document Uploads</Label>
                    <p className="text-xs text-muted-foreground">
                      When disabled, users cannot upload new documents.
                    </p>
                  </div>
                  <Switch
                    checked={uploadEnabled}
                    onCheckedChange={(checked) => {
                      setUploadEnabled(checked)
                      extractionService.setUploadEnabled(checked)
                      toast.success(checked ? 'Uploads enabled' : 'Uploads disabled')
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Environment Variables</CardTitle>
                <CardDescription>
                  Alternatively, you can set these as environment variables in your .env file
                </CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
{`VITE_NANONETS_API_KEY=your-api-key-here
VITE_NANONETS_MODEL_ID=your-model-id-here
VITE_UPLOAD_ENABLED=true`}
                </pre>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="billing" className="space-y-6">
            {showSuccessAlert && (
              <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-700 dark:text-green-300">
                  Your subscription has been activated successfully!
                </AlertDescription>
              </Alert>
            )}

            <SubscriptionStatus />

            <Card>
              <CardHeader>
                <CardTitle>Plans</CardTitle>
                <CardDescription>
                  Choose the plan that best fits your needs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PricingTable currentTier={subscription?.tier} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  )
}
